/**
 * Страница мастера заказа по экологии (`/orders/:id/ecology`, гард company, ecology-order). Загружает
 * каркас заказа, данные заказа по экологии, справочник услуг, вопросы опросника и площадки
 * организации и передаёт их в `EcologyOrderWizard`. Заказ другой услуги — мягкая ошибка.
 * `?section=` — подраздел, из которого начат заказ: шаг «Услуга» показывает только его услуги
 * (ecology-order-service-search); неизвестное значение игнорируется.
 */
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ecologyApi,
  ecologyClientApi,
  ecologyOrdersApi,
  ordersApi,
  type EcoCatalogItem,
  type EcoOrder,
  type EcoQuestion,
  type EcoSite,
  type EcoSiteItem,
  type OrderDetail,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { serviceLabel } from '@/lib/orders'
import { parseEcoSection } from '@/lib/ecology'
import { EcologyOrderWizard } from '@/ecology/order/order-wizard'
import { EcoOrderSummary } from '@/ecology/order/order-summary'
import { useAuth } from '@/app/auth-context'
import { AnketaCancelOrderButton, CancelledOrderAlert } from '@/orders/order-cancel'
import { OrderQuestionButton } from '@/questions/ask-question'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

interface Loaded {
  order: OrderDetail
  ecoOrder: EcoOrder
  catalog: EcoCatalogItem[]
  questions: EcoQuestion[]
  sites: EcoSite[]
  /** Строки раздела — из их карточек подставляются числа полей заявки. */
  items: EcoSiteItem[]
}

export function OrderEcologyWizardPage() {
  const navigate = useNavigate()
  const { refresh } = useAuth()
  // Анкета отправлена прямо сейчас — отменять уже со страницы заказа, не с экрана «Отправлено».
  const [sent, setSent] = useState(false)
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const section = parseEcoSection(searchParams.get('section'))
  const [data, setData] = useState<Loaded | null>(null)
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    ordersApi
      .get(id, controller.signal)
      .then(async (ord) => {
        if (controller.signal.aborted) return
        setOrder(ord)
        if (ord.service !== 'ecology') return
        const [ecoOrder, catalog, questions, overview] = await Promise.all([
          ecologyOrdersApi.get(id, controller.signal),
          ecologyApi.listCatalog(controller.signal),
          ecologyApi.listQuestions(controller.signal),
          ecologyClientApi.overview(controller.signal),
        ])
        if (controller.signal.aborted) return
        setData({
          order: ord,
          ecoOrder,
          catalog,
          questions,
          sites: overview.sites,
          items: overview.items,
        })
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id])

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/ecology')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К разделу «Экология»
        </Button>
      </div>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !order ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : order.service !== 'ecology' ? (
        <Alert variant="danger">
          Это заказ услуги «{serviceLabel(order.service)}» — мастер экологии к нему не применим.
        </Alert>
      ) : !data ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <PageHead
            title="Заказ услуги по экологии"
            subtitle="Услуга из справочника, площадки и вопросы по услуге — заявка уйдёт менеджеру Центрики"
            actions={
              sent ? undefined : (
                <div className="flex flex-wrap justify-end gap-2">
                  {/* Вопрос по заказу (client-questions; ТЗ п.9.7): у заказа «На заполнении» и
                      «Требуются правки» анкета и есть страница заказа. */}
                  <OrderQuestionButton order={order} onCreated={() => void refresh()} />
                  <AnketaCancelOrderButton order={order} onCancelled={() => void refresh()} />
                </div>
              )
            }
          />
          {order.dataStatus === 'changes_requested' && order.returnComment ? (
            <Alert variant="info" title="Требуются правки" className="mb-4">
              {order.returnComment}
            </Alert>
          ) : null}
          {order.dataStatus === 'cancelled' ? (
            <CancelledOrderAlert orderId={order.id} className="mb-4" />
          ) : null}
          {data.ecoOrder.canEdit || order.dataStatus === 'cancelled' ? null : (
            <Alert variant="info" title="Только просмотр" className="mb-4">
              Заказ уже отправлен — заявка доступна только для чтения. Ход работ виден в карточке
              заказа.
            </Alert>
          )}
          {data.ecoOrder.canEdit ? null : <EcoOrderSummary ecoOrder={data.ecoOrder} />}
          {data.ecoOrder.canEdit ? (
            <EcologyOrderWizard
              order={data.order}
              ecoOrder={data.ecoOrder}
              catalog={data.catalog}
              questions={data.questions}
              sites={data.sites}
              items={data.items}
              section={section}
              onSubmitted={() => {
                setSent(true)
                void refresh()
              }}
            />
          ) : null}
        </>
      )}
    </div>
  )
}
